import { useEffect, useState, useCallback } from "react"
import { getPayouts, approvePayout, cancelPayout } from "@/services/payout.service"
import { useToast } from "@/context/ToastContext"
import { useAuth } from "@/context/AuthContext"
import { Badge } from "@/components/common/Badge"
import { Button } from "@/components/common/Button"
import { Modal, FormGroup, Textarea } from "@/components/common/Modal"
import { EmptyState } from "@/components/common/EmptyState"
import { LoadingRow } from "@/components/common/Spinner"
import { RefreshCw, CheckCircle, XCircle, Zap } from "lucide-react"
import type { Payout } from "@/types"

type PayoutWithUser = Payout & { user_email: string; user_name: string | null }

const STATUS_FILTERS = [
  { value: "all",        label: "All Payouts" },
  { value: "pending",    label: "Pending" },
  { value: "processing", label: "Processing" },
  { value: "completed",  label: "Completed" },
  { value: "failed",     label: "Failed" },
  { value: "cancelled",  label: "Cancelled" },
]

export function PayoutsPage() {
  const { toast } = useToast()
  const { user }  = useAuth()
  const [payouts, setPayouts]   = useState<PayoutWithUser[]>([])
  const [loading, setLoading]   = useState(true)
  const [filter, setFilter]     = useState("all")
  const [acting, setActing]     = useState<string | null>(null)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [cancelOpen, setCancelOpen]     = useState(false)
  const [cancelTarget, setCancelTarget] = useState<string | null>(null)
  const [cancelReason, setCancelReason] = useState("")

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const data = await getPayouts(filter === "all" ? undefined : filter)
      setPayouts(data)
    } catch { toast("Failed to load payouts", "error") }
    finally { setLoading(false) }
  }, [filter, toast])

  useEffect(() => { load() }, [load])

  const pendingPayouts   = payouts.filter(p => p.status === "pending")
  const pendingAmount    = pendingPayouts.reduce((s, p) => s + (p.amount ?? 0), 0)
  const processingAmount = payouts.filter(p => p.status === "processing").reduce((s, p) => s + (p.amount ?? 0), 0)
  const completedAmount  = payouts.filter(p => p.status === "completed").reduce((s, p) => s + (p.amount ?? 0), 0)

  const handleApprove = async (payoutId: string) => {
    setActing(payoutId)
    try {
      await approvePayout(payoutId, user?.id ?? "")
      toast("Payout approved and moved to processing", "success")
      load()
    } catch (err: unknown) {
      toast(err instanceof Error ? err.message : "Failed to approve payout", "error")
    } finally { setActing(null) }
  }

  const handleCancel = async () => {
    if (!cancelTarget || !cancelReason) { toast("Reason required", "error"); return }
    setActing(cancelTarget)
    try {
      await cancelPayout(cancelTarget, cancelReason)
      toast("Payout cancelled", "success")
      setCancelOpen(false)
      setCancelReason("")
      load()
    } catch { toast("Failed to cancel payout", "error") }
    finally { setActing(null) }
  }

  const toggleSelect = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const selectAll = () => {
    if (selected.size === pendingPayouts.length) {
      setSelected(new Set())
    } else {
      setSelected(new Set(pendingPayouts.map(p => p.id)))
    }
  }

  return (
    <div className="space-y-5">
      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: "Pending",    value: "$" + pendingAmount.toFixed(2),    sub: pendingPayouts.length + " requests", color: "bg-amber-500"  },
          { label: "Processing", value: "$" + processingAmount.toFixed(2), sub: "In-flight",                        color: "bg-blue-500"   },
          { label: "Completed",  value: "$" + completedAmount.toFixed(2),  sub: "This period",                      color: "bg-green-500"  },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className={"h-1 " + s.color} />
            <div className="p-5">
              <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400 mb-1">{s.label}</p>
              <p className="text-2xl font-black text-gray-900 tabular-nums">{loading ? "—" : s.value}</p>
              <p className="text-xs text-gray-400 mt-1">{s.sub}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-3 flex-wrap">
        <select value={filter} onChange={e => setFilter(e.target.value)}
          className="px-3 py-2.5 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-zimrah-500 transition-all">
          {STATUS_FILTERS.map(f => (
            <option key={f.value} value={f.value}>{f.label}</option>
          ))}
        </select>
        <Button variant="secondary" icon={<RefreshCw size={14} />} onClick={load}>Refresh</Button>
        {selected.size > 0 && (
          <div className="flex items-center gap-2 px-4 py-2 bg-zimrah-50 border border-zimrah-200 rounded-xl">
            <Zap size={14} className="text-zimrah-600" />
            <span className="text-xs font-bold text-zimrah-700">{selected.size} selected</span>
            <Button size="sm" variant="primary"
              onClick={() => toast("Go to NACHA Batches page to create batch", "info")}>
              Create NACHA Batch
            </Button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-50 flex justify-between items-center">
          <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Payout Queue</h2>
          <div className="flex items-center gap-3">
            {pendingPayouts.length > 0 && (
              <button onClick={selectAll}
                className="text-xs font-semibold text-zimrah-600 hover:text-zimrah-800 transition-colors">
                {selected.size === pendingPayouts.length ? "Deselect all" : "Select all pending"}
              </button>
            )}
            <Badge variant="muted">{payouts.length} payouts</Badge>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-4 py-3 w-8" />
                {["Member", "Amount", "Method", "Status", "Requested", "Actions"].map(h => (
                  <th key={h} className="px-6 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-gray-400">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? <LoadingRow cols={7} /> :
               payouts.length === 0 ? (
                <tr><td colSpan={7}>
                  <EmptyState icon="💸" title="No payouts" subtitle="Payout requests will appear here." />
                </td></tr>
               ) : payouts.map(p => (
                <tr key={p.id}
                  className={"border-t border-gray-50 transition-colors " + (selected.has(p.id) ? "bg-zimrah-50/50" : "hover:bg-gray-50/50")}>
                  <td className="px-4 py-4">
                    {p.status === "pending" && (
                      <input type="checkbox" checked={selected.has(p.id)}
                        onChange={() => toggleSelect(p.id)}
                        className="w-4 h-4 rounded border-gray-300 text-zimrah-600 focus:ring-zimrah-500" />
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <p className="font-bold text-sm text-gray-900">{p.user_name ?? "—"}</p>
                    <p className="text-xs text-gray-400">{p.user_email}</p>
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-2xl font-black text-gray-900 tabular-nums">${(p.amount ?? 0).toFixed(2)}</p>
                  </td>
                  <td className="px-6 py-4">
                    <Badge variant="info">{(p.method ?? "ach").toUpperCase()}</Badge>
                  </td>
                  <td className="px-6 py-4">
                    {p.status === "pending"    ? <Badge variant="warning"  dot>Pending</Badge>    :
                     p.status === "processing" ? <Badge variant="info"     dot>Processing</Badge> :
                     p.status === "completed"  ? <Badge variant="success"  dot>Completed</Badge>  :
                     p.status === "failed"     ? <Badge variant="danger"   dot>Failed</Badge>     :
                     <Badge variant="muted" dot>Cancelled</Badge>}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-400 whitespace-nowrap">
                    {new Date(p.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex gap-1">
                      {p.status === "pending" && (
                        <>
                          <Button size="sm" variant="success" icon={<CheckCircle size={13} />}
                            loading={acting === p.id}
                            onClick={() => handleApprove(p.id)}>
                            Approve
                          </Button>
                          <Button size="sm" variant="ghost" icon={<XCircle size={13} />}
                            onClick={() => { setCancelTarget(p.id); setCancelOpen(true) }}>
                            Cancel
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
               ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Cancel Modal */}
      <Modal open={cancelOpen} onClose={() => setCancelOpen(false)} title="Cancel Payout" icon="❌"
        footer={
          <>
            <Button variant="secondary" onClick={() => setCancelOpen(false)}>Back</Button>
            <Button variant="danger" loading={acting === cancelTarget} onClick={handleCancel}>
              Cancel Payout
            </Button>
          </>
        }
      >
        <FormGroup label="Cancellation Reason" required>
          <Textarea value={cancelReason} onChange={e => setCancelReason(e.target.value)}
            placeholder="Why is this payout being cancelled?" />
        </FormGroup>
        <div className="p-3 rounded-xl bg-amber-50 border border-amber-100">
          <p className="text-xs font-semibold text-amber-700">
            The payout amount will remain in the member wallet. This action is logged.
          </p>
        </div>
      </Modal>
    </div>
  )
}